import {
    LONG_PLAIN_TEXT_LENGTH,
    LONG_RICH_TEXT_LENGTH,
    MAX_CUSTOM_HTML_PAGE_FILE_SIZE,
    MEDIUM_PASS_CODE_LENGTH,
    MEDIUM_PLAIN_TEXT_LENGTH,
    MIN_PASS_CODE_LENGTH,
    MIN_PODIUM_ACCESS_CODE_LENGTH,
    SHORT_PLAIN_TEXT_LENGTH,
    SHORT_RICH_TEXT_LENGTH
} from '../constants/common.constants';
import { DOMAIN_VALIDATION_REG_EXP, EMAIL_VALIDATION_REG_EXP, LINK_REG_EXP, SUBDOMAIN_REG_EXP } from '../constants/regex.constants';
import { FormVmValueValidatorResultType, FormVmValueValidatorType, InitialData } from '../infrastructure/form-viewmodel-controller';
import { getCommaSeparatedStringList } from '../infrastructure/utils/common.utils';

type isValidType = (value: unknown, ...args: unknown[]) => boolean;
type InputValidatorType = (value?: unknown, ...args: (string | number)[]) => FormVmValueValidatorResultType;

export const stringValueRequired: isValidType = (val: string): boolean => [val, typeof val === 'string', val?.length].every(Boolean);
export const integerValueRequired: isValidType = (val: number): boolean => [val, Number.isInteger(val), val > 0].every(Boolean);
export const integerValueInRange: isValidType = (val: number, min: number, max: number): boolean => Number.isInteger(val) && val >= min && val <= max;
const stringValueMaxCharAllowed: isValidType = (val: string, maxChar: number): boolean => val ? val.length <= maxChar : true;
const stringValueMinCharAllowed: isValidType = (val: string, minChar: number): boolean => val ? val.length >= minChar : true;


export const requiredValidator: InputValidatorType = (val: string = ''): FormVmValueValidatorResultType => {
    const valid = stringValueRequired(val);
    const validationMessage = valid ? null : { id: 'oe.validation.required.message', defaultMessage: '{fieldName} is required.' };

    return { valid: valid, validationMessage: validationMessage };
};

export const requiredIdValidator: InputValidatorType = (val: number = null): FormVmValueValidatorResultType => {
    const valid = !!val;
    const validationMessage = valid ? null : { id: 'oe.validation.required.message', defaultMessage: '{fieldName} is required.' };

    return { valid: valid, validationMessage: validationMessage };
};

export const requiredIntegerValidator: InputValidatorType = (val: number = null): FormVmValueValidatorResultType => {
    const valid = integerValueRequired(Number(val));
    const validationMessage = valid ? null : { id: 'oe.validation.validId.message', defaultMessage: '{fieldName} should be valid ID.' };

    return { valid: valid, validationMessage: validationMessage };
};

export const conditionalIntegerRangeValidator = (
    min: number,
    max: number,
    isEnabled: (values: InitialData) => boolean
): FormVmValueValidatorType => (val, values = {}): FormVmValueValidatorResultType => {
    if (!isEnabled(values)) {
        return { valid: true, validationMessage: null };
    }

    const isEmpty = val === '';
    if (isEmpty) {
        const requiredMessage = { id: 'oe.validation.requiredIfEnabled.message', defaultMessage: 'The value is required if enabled.' };

        return { valid: false, validationMessage: requiredMessage };
    }

    const valid = integerValueInRange(Number(val), min, max);
    const validationMessage = valid
        ? null
        : { id: 'oe.validation.integerRange.message', defaultMessage: 'The value must be between {min} and {max}.', values: { min: min, max: max } };

    return { valid: valid, validationMessage: validationMessage };
};

export const requiredFileValidator: InputValidatorType = (val: File): FormVmValueValidatorResultType => {
    const valid = !!val;
    const validationMessage = valid ? null : { id: 'oe.error.required' };

    return { valid: valid, validationMessage: validationMessage };
};

export const maxCharactersValidator: InputValidatorType = (val: string = '', maxChar: number): FormVmValueValidatorResultType => {
    const valid = stringValueMaxCharAllowed(val, maxChar);
    const validationMessage = valid
        ? null :
        { id: 'oe.validation.tooLong.message', defaultMessage: `Too long - max length is ${maxChar} characters.` };

    return { valid: valid, validationMessage: validationMessage };
};

export const minCharactersValidator: InputValidatorType = (val: string = '', minChar: number): FormVmValueValidatorResultType => {
    const valid = stringValueMinCharAllowed(val, minChar);
    const validationMessage = valid
        ? null :
        { id: 'oe.validation.tooShort.message', defaultMessage: `Too short - min length is ${minChar} characters.` };

    return { valid: valid, validationMessage: validationMessage };
};

export const domainListValidator = (val: string): { valid: boolean; validationMessage?: { id: string; defaultMessage: string } } => {
    const domains = val.split(',').map(d => d.trim()).filter(Boolean);
    const invalidDomains = domains.filter(domain => !DOMAIN_VALIDATION_REG_EXP.test(domain));
    const valid = domains.length !== 0 && invalidDomains.length === 0;
    const validationMessage = valid
        ? null
        : domains.length === 0 ?
        {
            id: 'oe.domain.invalidDomain',
            defaultMessage: 'Managed Domains is a required field',
        }
        : {
            id: 'oe.domain.invalidDomain',
            defaultMessage: `${invalidDomains.join(', ')} ${invalidDomains.length > 1 ? 'are' : 'is'} not a valid ${invalidDomains.length > 1 ? `domain's` : 'domain'}.`,
        };

    return { valid: valid, validationMessage: validationMessage };
};

const urlValidator: InputValidatorType = (val: string = ''): FormVmValueValidatorResultType => {
    const valid = val ? LINK_REG_EXP.test(val) : true;
    const validationMessage = valid ? null : { id: 'oe.url.enterValidWith' };

    return { valid: valid, validationMessage: validationMessage };
};

const liveStreamUrlValidator: InputValidatorType = (val: string = ''): FormVmValueValidatorResultType => {
    const valid = val ? val.startsWith('rtmp://') : true;
    const validationMessage = valid ? null : { id: 'oe.liveStreamUrl.enterValidUrl' };

    return { valid: valid, validationMessage: validationMessage };
};

const fileSizeValidator: InputValidatorType = (val: File, size: number, sizeString: string): FormVmValueValidatorResultType => {
    const valid = val && val.size ? val.size <= size : true;
    const validationMessage = valid ? null : { id: 'oe.error.fileSizeInvalid', values: { value: sizeString } };

    return { valid: valid, validationMessage: validationMessage };
};

const emailValidator: InputValidatorType = (val: string): FormVmValueValidatorResultType => {
    const valid = val ? EMAIL_VALIDATION_REG_EXP.test(val) : true;
    const validationMessage = valid ? null : { id: `oe.email.emailIsNotValid`, defaultMessage: 'Must be a valid email address' };

    return { valid: valid, validationMessage: validationMessage };
};

const emailListValidator: InputValidatorType = (val: string, initial: InitialData, exceptions: string): FormVmValueValidatorResultType => {
    const emails = getCommaSeparatedStringList(val);
    const exceptionsList = getCommaSeparatedStringList(exceptions);
    const invalidEmails = emails.filter(item => !EMAIL_VALIDATION_REG_EXP.test(item.trim()) && !exceptionsList.includes(item.trim()));
    const valid = !invalidEmails.length;
    const validationMessage = valid
        ? null
        : {
            id: `oe.email.particularEmailIsNotValid`,
            defaultMessage: `${invalidEmails.join(', ')} is not a valid email address`.trim(),
        };

    return { valid: valid, validationMessage: validationMessage };
};

const fileTypeValidator: InputValidatorType = (val: File, type: string, sizeString: string): FormVmValueValidatorResultType => {
    const valid = val && val.name.endsWith(type);
    const validationMessage = valid ? null : { id: `oe.file.typeNotValid`, defaultMessage: 'Must be a {prefix} File', values: { prefix: sizeString } };

    return { valid: valid, validationMessage: validationMessage };
};

const subdomainValidator: InputValidatorType = (val: string = ''): FormVmValueValidatorResultType => {
    const validationMessages = [
        '- The subdomain must only contain valid characters: a-z (lowercase only), 0-9, and hyphen/dash ("-").',
        '- The subdomain must not begin or end with a hyphen/dash.',
        '- The subdomain must be between 3 and 63 characters.'
    ];
    const valid = val ? !!val.match(SUBDOMAIN_REG_EXP) : true;
    const validationMessage = valid
        ? null
        : { id: 'oe.validation.subdomainInvalid.message', defaultMessage: validationMessages.join('\n') };

    return { valid: valid, validationMessage: validationMessage };
};

const plainText = {
    required: requiredValidator,
    maxLength: {
        medium: (val: string): FormVmValueValidatorResultType => maxCharactersValidator(val, MEDIUM_PLAIN_TEXT_LENGTH),
        short: (val: string): FormVmValueValidatorResultType => maxCharactersValidator(val, SHORT_PLAIN_TEXT_LENGTH),
        long: (val: string): FormVmValueValidatorResultType => maxCharactersValidator(val, LONG_PLAIN_TEXT_LENGTH)
    }
};

const passCode = {
    required: requiredValidator,
    maxLength: {
        medium: (val: string): FormVmValueValidatorResultType => maxCharactersValidator(val, MEDIUM_PASS_CODE_LENGTH),
        short: (val: string): FormVmValueValidatorResultType => maxCharactersValidator(val, MIN_PASS_CODE_LENGTH)
    },
    minLength: {
        short: (val: string): FormVmValueValidatorResultType => minCharactersValidator(val, MIN_PODIUM_ACCESS_CODE_LENGTH)
    }
};

const richText = {
    required: requiredValidator,
    maxLength: {
        short: (val: string): FormVmValueValidatorResultType => maxCharactersValidator(val, SHORT_RICH_TEXT_LENGTH),
        long: (val: string): FormVmValueValidatorResultType => maxCharactersValidator(val, LONG_RICH_TEXT_LENGTH)
    }
};

const file = {
    required: requiredFileValidator,
    maxSize: {
        zip: (val: File): FormVmValueValidatorResultType => fileSizeValidator(val, MAX_CUSTOM_HTML_PAGE_FILE_SIZE, '20mb')
    },
    fileType: {
        zip: (val: File): FormVmValueValidatorResultType => fileTypeValidator(val, '.zip', 'Zip')
    }
};

const url = {
    regExp: urlValidator
};

const liveStreamUrl = {
    rtmp: liveStreamUrlValidator
};

const email = {
    regExp: emailValidator
};

const emailList = {
    regExp: emailListValidator
};

export {
    InputValidatorType, isValidType,
    plainText, richText, passCode, url, liveStreamUrl, file, email, emailList,
    emailListValidator, subdomainValidator
};
